import { Laptop, Trash2 } from "lucide-react";
import { useState } from "react";

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogTitle,
  Badge,
  Button,
  Card,
  EmptyState,
  PageHeader,
  Skeleton,
  StatusMessage,
  Tooltip,
} from "@ceedero/ui";

import { useDevices, useRemoveDevice, type Device } from "@/devices/hooks/use-devices";
import { AppPage } from "@/components/app-page";
import {
  describeDeviceName,
  describeDevicePlatform,
  formatActivationDate,
} from "@/devices/utils/format-device";
import { useTranslation } from "@/localization/hooks/use-translation";

/**
 * The devices this licence is activated on, and the way to free a slot.
 *
 * None of them is marked as "this one". Only an installation knows the
 * activation id it holds, and a browser is not an installation — guessing from
 * an address or a user agent would be a fingerprint, wrong behind a shared
 * connection, and worth a label at best.
 */
export const DevicesPage = () => {
  const { language, t } = useTranslation();
  const devices = useDevices();
  const remove = useRemoveDevice();
  const [deviceToRemove, setDeviceToRemove] = useState<Device | undefined>(undefined);

  const confirmRemoval = () => {
    if (!deviceToRemove) {
      return;
    }
    remove.mutate(deviceToRemove.activationId);
    setDeviceToRemove(undefined);
  };

  return (
    <AppPage
      width="extraWide"
      header={
        <PageHeader
          title={t("devices.title")}
          description={t("devices.description")}
          actions={
            devices.data?.deviceLimit == null ? undefined : (
              <Badge tone="neutral">
                {t("devices.limit", {
                  total: devices.data.deviceLimit,
                  used: devices.data.devices.length,
                })}
              </Badge>
            )
          }
        />
      }
    >
      {devices.isPending && <Skeleton className="h-24 w-full" />}

      {/*
        Polar owns this list and it is never served from a cache, so failing to
        reach it must read as "could not load" and never as "you have none".
      */}
      {devices.isError && <StatusMessage tone="error">{t("devices.unavailable")}</StatusMessage>}

      {devices.data?.devices.length === 0 && (
        <EmptyState
          icon={<Laptop />}
          title={t("devices.empty.title")}
          description={t("devices.empty.description")}
        />
      )}

      {devices.data && devices.data.devices.length > 0 && (
        <ul className="flex flex-col gap-2">
          {devices.data.devices.map((device) => {
            const name = describeDeviceName(device, t("devices.unnamed"));
            const platform = describeDevicePlatform(device);
            const activated = formatActivationDate(device.activatedAt, language);
            const isRemoving = remove.isPending && remove.variables === device.activationId;
            return (
              <li key={device.activationId}>
                <Card className="flex flex-wrap items-center justify-between gap-3 p-4">
                  <div className="flex min-w-0 items-center gap-3">
                    <Laptop className="size-5 shrink-0 text-muted-foreground" aria-hidden />
                    <div className="min-w-0">
                      <p className="truncate font-medium">{name}</p>
                      <p className="text-xs text-muted-foreground">
                        {[
                          platform,
                          activated === undefined
                            ? undefined
                            : t("devices.activated", { date: activated }),
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </p>
                    </div>
                  </div>
                  {/*
                    The row's only action, so it is the desktop's icon-sized
                    `destructiveGhost` rather than a filled button competing
                    with the device's own name for the eye. The glyph carries
                    the meaning for a screen reader through `aria-label`, and
                    the tooltip puts the same word on screen for everybody else
                    — above the button, where it cannot cover the next row.
                  */}
                  <Tooltip content={t("devices.remove")} side="top">
                    <Button
                      type="button"
                      variant="destructiveGhost"
                      size="icon"
                      className="h-8 w-8"
                      aria-label={t("devices.removeDevice", { name })}
                      isLoading={isRemoving}
                      onClick={() => setDeviceToRemove(device)}
                    >
                      {isRemoving ? undefined : <Trash2 />}
                    </Button>
                  </Tooltip>
                </Card>
              </li>
            );
          })}
        </ul>
      )}

      {/*
        Asked about rather than done outright: this frees a slot on a device
        the user cannot see from here, and the confirmation is where it can be
        said that nothing is taken away from that device.
      */}
      <AlertDialog
        open={deviceToRemove !== undefined}
        onOpenChange={(open) => {
          if (!open) {
            setDeviceToRemove(undefined);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogTitle>{t("devices.removeTitle")}</AlertDialogTitle>
          <AlertDialogDescription>{t("devices.removeDescription")}</AlertDialogDescription>
          <AlertDialogFooter>
            <AlertDialogCancel asChild>
              <Button type="button" variant="ghost">
                {t("devices.cancel")}
              </Button>
            </AlertDialogCancel>
            <Button type="button" variant="destructive" onClick={confirmRemoval}>
              {t("devices.remove")}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppPage>
  );
};
